// d:\pos_web\src\pages\stores\StoreForm.js
import React, { useState, useEffect } from 'react';
import InputField from '../../components/InputField';
import CheckboxField from '../../components/CheckboxField';
import Button from '../../components/Button';
import ErrorMessage from '../../components/common/ErrorMessage';
import LoadingSpinner from '../../components/common/LoadingSpinner'; // Added
import useApi from '../../hooks/useApi'; // Added

const StoreForm = ({
  'store': initialData, // Expect 'manage store' prop and alias to initialData
  onClose,     // To close the form/modal
  onSaveSuccess, // To notify parent on successful save
}) => {
  const api = useApi(); // Added
  const [formData, setFormData] = useState({
    name: '',
    code: '', // Added
    location: '',
    contactPerson: '',
    contactPhone: '',
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false); // Added
  const [loading, setLoading] = useState(false);   // Added for submission loading
  const [error, setError] = useState(null);       // Added for submission errors

  useEffect(() => {
    if (initialData) {
      setIsEditing(true); // Added
      setFormData({
        name: initialData.name || '',
        code: initialData.code || '', // Added
        location: initialData.location || '',
        contactPerson: initialData.contactPerson || '',
        contactPhone: initialData.contactPhone || '',
        isActive: initialData.isActive !== undefined ? initialData.isActive : true,
      });
    } else {
      // Reset for new form
      setFormData({
        name: '',
        code: '', // Added
        location: '',
        contactPerson: '',
        contactPhone: '',
        isActive: true,
      });
      setIsEditing(false); // Added
    }
    setFormErrors({}); // Clear errors when initialData changes or form is reset
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Store name is required.';
    // Add more validations as needed (e.g., phone format)
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditing) {
        await api.put(`stores/${initialData._id}`, formData);
        alert("Store updated successfully!");
      } else {
        await api.post('stores', formData);
        alert("Store created successfully!");
      }
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Operation failed. Please try again.");
      console.error("Store form submission error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">

      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Store Name"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={formErrors.name}
          required
        />
        {isEditing && (
          <InputField
            label="Store Code"
            id="code"
            name="code"
            value={formData.code}
            disabled
            readOnly
          />
        )}
      </div>
      <InputField
        label="Location (Address)"
        id="location"
        name="location"
        value={formData.location}
        onChange={handleChange}
        error={formErrors.location}
      />
      <InputField
        label="Contact Person"
        id="contactPerson"
        name="contactPerson"
        value={formData.contactPerson}
        onChange={handleChange}
      />
      <InputField
        label="Contact Phone"
        id="contactPhone"
        name="contactPhone"
        type="tel"
        value={formData.contactPhone}
        onChange={handleChange}
      />
      <CheckboxField
        label="Is Active"
        id="isActive"
        name="isActive"
        checked={formData.isActive}
        onChange={handleChange}
      />

      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" onClick={onClose} variant="secondary" disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isLoading={loading} disabled={loading}>
          {initialData ? 'Save Changes' : 'Add Store'}
        </Button>
      </div>
    </form>
  );
};

export default StoreForm;